import React, { useEffect, useState } from "react";
import { SitePages } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUpload from "@/components/ImageUpload";
import MediaUpload from "@/components/MediaUpload";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Copy, ExternalLink, Sparkles, Download } from "lucide-react";
import { toast } from "sonner";
import { PAGE_TEMPLATES } from "./pageTemplates";
import LivePreviewPane from "@/components/admin/LivePreviewPane";
import SitePagePreview from "@/components/admin/SitePagePreview";
import { notifyPreviewRefresh } from "@/components/admin/GlobalPreviewPane";

const empty = {
  slug: "", title: "", meta_description: "",
  hero_eyebrow: "", hero_title: "", hero_subtitle: "", hero_image_url: "",
  intro_video_url: "",
  content_html: "", seo_schema_json: "",
  is_published: true, show_in_nav: false, sort_order: 0,
};

const SitePagesManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await SitePages.list(false)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit("new"); setForm(empty); };
  const openEdit = (p) => { setEdit(p.id); setForm({ ...empty, ...p }); };
  const close = () => { setEdit(null); setForm(empty); };

  const applyTemplate = (key) => {
    const tpl = PAGE_TEMPLATES[key];
    if (!tpl) return;
    setForm((f) => tpl.apply(f));
    if (key !== "blank") toast.success(`${tpl.label} applied`);
  };

  const save = async ({ keepOpen = false } = {}) => {
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      let saved;
      if (edit === "new") saved = await SitePages.create(form);
      else saved = await SitePages.update(edit, form);
      toast.success("Saved");
      notifyPreviewRefresh();
      if (keepOpen) {
        // Stay in editor; if this was a "new" page, switch into edit mode
        // for the freshly-created record so future saves are updates.
        if (saved && saved.id) {
          setEdit(saved.id);
          setForm({ ...empty, ...saved });
        }
        load(); // refresh underlying list silently
      } else {
        close();
        load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete page "${p.title}"?`)) return;
    try { await SitePages.remove(p.id); toast.success("Deleted"); notifyPreviewRefresh(); load(); }
    catch { toast.error("Delete failed"); }
  };

  const importStarters = async () => {
    if (!window.confirm("Import the 8 ATC starter service pages (Mold Removal, Mold Testing, Water Damage, Sewage, Crawl Space, Storm, Fire, About)? Existing pages with the same slug will be left untouched.")) return;
    setImporting(true);
    try {
      const res = await SitePages.importStarters();
      if (res.created.length) toast.success(`Imported ${res.created.length} page${res.created.length === 1 ? "" : "s"}`);
      if (res.skipped.length) toast(`Skipped ${res.skipped.length} (already existed): ${res.skipped.join(", ")}`);
      if (!res.created.length && !res.skipped.length) toast("Nothing to import");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const copyUrl = (slug) => {
    const url = `${window.location.origin}/p/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("URL copied");
  };

  return (
    <div className="p-10" data-testid="site-pages-manager">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-2">Custom pages</div>
          <h1 className="font-display font-black text-4xl text-white">Site Pages</h1>
          <p className="text-slate-400 text-sm mt-2">
            Create new pages at <code className="text-atc-blue">/p/&lt;slug&gt;</code> — useful for service detail, FAQ, blog posts, etc.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={importStarters}
            disabled={importing}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-atc-blue/40 text-atc-blue hover:bg-atc-blue/10 text-sm font-medium disabled:opacity-50"
            data-testid="import-starter-pages-button"
            title="Insert the 8 standard ATC service pages (idempotent — skips existing slugs)"
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Import 8 Starter Pages
          </button>
          <button onClick={openNew} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full brand-gradient text-white text-sm font-semibold" data-testid="new-site-page-button">
            <Plus className="h-4 w-4" /> New Page
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center text-slate-400">
          No custom pages yet. Click "New Page" to create one.
        </div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-[0.16em]">
              <tr>
                <th className="text-left px-5 py-3">Title</th>
                <th className="text-left px-5 py-3">URL</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">In nav</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-slate-900/50" data-testid={`site-page-row-${p.slug}`}>
                  <td className="px-5 py-4 text-white">{p.title}</td>
                  <td className="px-5 py-4">
                    <code className="text-xs text-slate-300">/p/{p.slug}</code>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${p.is_published ? "bg-atc-green/15 text-atc-green" : "bg-slate-700/30 text-slate-400"}`}>
                      {p.is_published ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs text-slate-400">{p.show_in_nav ? "Yes" : "No"}</td>
                  <td className="px-5 py-4 text-right space-x-3">
                    <button onClick={() => copyUrl(p.slug)} className="text-slate-400 hover:text-white" aria-label={`Copy URL for ${p.title}`} title="Copy URL" data-testid={`copy-${p.slug}`}>
                      <Copy className="h-4 w-4 inline" />
                    </button>
                    <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white" aria-label={`Open ${p.title} in new tab`} title="Open in new tab">
                      <ExternalLink className="h-4 w-4 inline" />
                    </a>
                    <button onClick={() => openEdit(p)} className="text-slate-300 hover:text-white" aria-label={`Edit ${p.title}`} title="Edit" data-testid={`edit-site-page-${p.slug}`}>
                      <Pencil className="h-4 w-4 inline" />
                    </button>
                    <button onClick={() => remove(p)} className="text-slate-300 hover:text-rose-400" aria-label={`Delete ${p.title}`} title="Delete" data-testid={`delete-site-page-${p.slug}`}>
                      <Trash2 className="h-4 w-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-slate-950 border-white/10 text-white max-w-[1400px] w-[95vw] max-h-[92vh] overflow-hidden p-0 flex flex-col">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/5 shrink-0">
            <DialogTitle>{edit === "new" ? "New page" : "Edit page"}</DialogTitle>
          </DialogHeader>
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-5 px-6 py-5 overflow-y-auto flex-1">
          <div className="space-y-5 min-w-0">
            {edit === "new" && (
              <div className="rounded-xl bg-atc-blue/5 border border-atc-blue/30 p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-atc-blue mb-2">
                  <Sparkles className="h-3.5 w-3.5" /> Start from a template
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(PAGE_TEMPLATES).map(([key, tpl]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => applyTemplate(key)}
                      className="px-3 py-2 rounded-lg bg-slate-950/60 border border-white/10 hover:border-atc-blue/60 text-left transition"
                      data-testid={`tpl-${key}`}
                    >
                      <div className="text-sm text-white font-medium">{tpl.label}</div>
                      <div className="text-[10px] text-slate-400">{tpl.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Title</label>
                <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="bg-slate-950 border-white/10 mt-1" data-testid="sp-title" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Slug (URL — optional)</label>
                <Input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} placeholder={form.title ? "auto-generated" : ""} className="bg-slate-950 border-white/10 mt-1" data-testid="sp-slug" />
                <div className="text-[10px] text-slate-500 mt-1">Page will live at /p/<span className="text-atc-blue">{form.slug || (form.title.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'') || 'your-slug')}</span></div>
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Meta description (SEO)</label>
              <Textarea rows={2} value={form.meta_description} onChange={(e) => setForm((f) => ({ ...f, meta_description: e.target.value }))} className="bg-slate-950 border-white/10 mt-1" data-testid="sp-meta" />
            </div>

            <div className="rounded-xl border border-white/10 p-4 space-y-4">
              <div className="text-xs uppercase tracking-[0.18em] text-atc-blue">Hero (optional)</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder="Eyebrow (small text above title)" value={form.hero_eyebrow} onChange={(e) => setForm((f) => ({ ...f, hero_eyebrow: e.target.value }))} className="bg-slate-950 border-white/10" data-testid="sp-hero-eyebrow" />
                <Input placeholder="Hero title (defaults to page title)" value={form.hero_title} onChange={(e) => setForm((f) => ({ ...f, hero_title: e.target.value }))} className="bg-slate-950 border-white/10" data-testid="sp-hero-title" />
              </div>
              <Textarea rows={2} placeholder="Hero subtitle" value={form.hero_subtitle} onChange={(e) => setForm((f) => ({ ...f, hero_subtitle: e.target.value }))} className="bg-slate-950 border-white/10" data-testid="sp-hero-subtitle" />
              <ImageUpload
                value={form.hero_image_url}
                onChange={(url) => setForm((f) => ({ ...f, hero_image_url: url }))}
                testId="sp-hero-image"
                label="Hero background image"
              />
            </div>

            <div className="rounded-xl border border-atc-blue/25 p-4 space-y-2 bg-atc-blue/[0.03]">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.18em] text-atc-blue">Intro video pop-up (optional)</div>
                {form.intro_video_url && (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, intro_video_url: "" }))}
                    className="text-[10px] uppercase tracking-wide text-rose-400 hover:text-rose-300"
                    data-testid="sp-clear-intro-video"
                  >
                    Remove
                  </button>
                )}
              </div>
              <p className="text-[11px] text-slate-400">
                Upload an mp4/webm/mov <em>or</em> paste a YouTube/Vimeo link. When a visitor lands on this page the video will pop up centered and play once with sound. Auto-shows once per visitor session.
              </p>
              <MediaUpload
                value={form.intro_video_url}
                onChange={(url) => setForm((f) => ({ ...f, intro_video_url: url }))}
                testId="sp-intro-video"
                kind="any"
                label="Intro video (file or YouTube/Vimeo URL)"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400 block mb-2">Page content</label>
              <RichTextEditor
                value={form.content_html}
                onChange={(html) => setForm((f) => ({ ...f, content_html: html }))}
                placeholder="Write the page body — use the toolbar for headings, lists, links, images, and video embeds…"
                minHeight={260}
                testId="sp-content-editor"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400 block mb-1">
                SEO structured data (JSON-LD)
              </label>
              <div className="text-[10px] text-slate-500 mb-2">
                Optional. Paste a schema.org JSON object (e.g. FAQPage, Service, Article). It's injected as
                <code className="text-atc-blue mx-1">&lt;script type="application/ld+json"&gt;</code> on the page.
              </div>
              <Textarea
                rows={4}
                value={form.seo_schema_json}
                onChange={(e) => setForm((f) => ({ ...f, seo_schema_json: e.target.value }))}
                className="bg-slate-950 border-white/10 text-xs font-mono"
                placeholder='{"@context":"https://schema.org",...}'
                data-testid="sp-schema"
              />
            </div>

            <div className="grid sm:grid-cols-3 gap-4 items-end">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_published} onCheckedChange={(v) => setForm((f) => ({ ...f, is_published: v }))} data-testid="sp-published" />
                <span className="text-sm text-slate-300">Published</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.show_in_nav} onCheckedChange={(v) => setForm((f) => ({ ...f, show_in_nav: v }))} data-testid="sp-show-nav" />
                <span className="text-sm text-slate-300">Show in footer</span>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Sort order</label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))} className="bg-slate-950 border-white/10 mt-1" data-testid="sp-sort" />
              </div>
            </div>
          </div>

          {/* Live preview pane */}
          <div className="hidden lg:block sticky top-0 self-start h-[calc(92vh-9rem)]">
            <LivePreviewPane
              title="Live preview · /p/"
              openUrl={form.slug ? `/p/${form.slug}` : undefined}
              testId="site-page-live-preview"
            >
              <SitePagePreview page={form} />
            </LivePreviewPane>
          </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-white/5 shrink-0">
            <button onClick={close} className="px-4 py-2 rounded-full text-sm text-slate-300 hover:text-white">Cancel</button>
            <button onClick={() => save({ keepOpen: true })} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium text-slate-200 border border-white/15 hover:bg-slate-900/50 disabled:opacity-60" data-testid="sp-save-keep">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save & keep editing
            </button>
            <button onClick={() => save()} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 rounded-full brand-gradient text-white text-sm font-semibold disabled:opacity-60" data-testid="sp-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save & close
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SitePagesManager;
